<script setup lang="ts">
import DisplayDeliveryPlanInformation from '@/components/DailyReportEdit/Split/DisplayDeliveryPlanInformation.vue';
import DailyReportSplitFormDetail from '@/components/DailyReportEdit/Split/SplitFormDetail.vue';
import getDeliveryPlanVehicleInformation from '@/types/responses/getDeliveryPlanVehicleInformation';
import divideDailyReportsResponse from '@/types/responses/divideDailyReports';
import divideDailyReportsRequest from '@/types/requests/divideDailyReports';
import RegisterModal from '@/components/DailyReportEdit/Split/RegisterModal.vue';
import CompleteModal from '@/components/DailyReportEdit/Split/CompleteModal.vue';
import ValidationErrors from '@/components/Common/ValidationErrors.vue';
import { formatDateTimeWithSeconds } from '@/services/functions';
import { useAuth } from '@/services/user/Auth0UserProvider';
import Api from '@/services/api/ApiServiceFabrick';
import { useRouter } from 'vue-router';
import { ref, Ref, onMounted, computed, watch } from 'vue';
import i18n from '@/lang/i18n';

type splitReturningAndLeavingTimeType = {
  splitReturningTime: string | null;
  splitLeavingTime: string | null;
};
const { loginWithRedirect } = useAuth();
const router = useRouter();
const splitReturningAndLeavingTime = ref({
  splitReturningTime: null,
  splitLeavingTime: null
} as splitReturningAndLeavingTimeType);
const isLoading: Ref<boolean> = ref(false);
const isRequested: Ref<boolean> = ref(false);
const errorMessages = ref<{
  returning_warehouse_date_time: string[]
  leaving_warehouse_date_time: string[]
  delivery_plan_id: string[]
}>({
  returning_warehouse_date_time: [],
  leaving_warehouse_date_time: [],
  delivery_plan_id: []
});
const isOpenDialog: Ref<boolean> = ref(false);
const isDisplayCompleteModal: Ref<boolean> = ref(false);

const props = defineProps<{
  showFlash: (message: string, type: string, fadeout: boolean) => void
  deliveryPlanId: string
  deliveryPlan: getDeliveryPlanVehicleInformation
}>();

onMounted(() => {
  setLeavingAndReturning();
});

const isActiveExecBtn = computed(() => {
  return !isRequested.value && isRequestableSplitReturningTime() && isRequestableSplitLeavingTime()
})

const execBtnClass = computed(() => {
  return {
    'exec-btn-disabled': !isActiveExecBtn.value
  };
});

function setLeavingAndReturning() {
  splitReturningAndLeavingTime.value.splitReturningTime = props.deliveryPlan.leaving_warehouse_date_time;
  splitReturningAndLeavingTime.value.splitLeavingTime = props.deliveryPlan.returning_warehouse_date_time;
}

function isRequestableSplitReturningTime() {
  return splitReturningAndLeavingTime.value.splitReturningTime
    && splitReturningAndLeavingTime.value.splitLeavingTime
    && new Date(splitReturningAndLeavingTime.value.splitReturningTime) > new Date(props.deliveryPlan.leaving_warehouse_date_time)
    && new Date(splitReturningAndLeavingTime.value.splitReturningTime) < new Date(splitReturningAndLeavingTime.value.splitLeavingTime);
}

function isRequestableSplitLeavingTime() {
  return splitReturningAndLeavingTime.value.splitReturningTime
    && splitReturningAndLeavingTime.value.splitLeavingTime
    && new Date(splitReturningAndLeavingTime.value.splitLeavingTime) < new Date(props.deliveryPlan.returning_warehouse_date_time)
    && new Date(splitReturningAndLeavingTime.value.splitLeavingTime) > new Date(splitReturningAndLeavingTime.value.splitReturningTime);
}

function execSplit() {
  isLoading.value = true;
  Api().divideDailyReports(props.deliveryPlanId, makeRequest(splitReturningAndLeavingTime.value)).then((data: divideDailyReportsResponse) => {
    if (data.status) {
      return isDisplayCompleteModal.value = true;
    }
    props.showFlash(i18n.global.t('pages.dailyReports.splitForm.failedMessage'), 'error', true);
  }).catch((error) => {
    errorHandler(error);
  }).finally(() => {
    isLoading.value = false;
  });
}

function makeRequest(returningAndLeavingTime: splitReturningAndLeavingTimeType): divideDailyReportsRequest {
  return {
    returning_warehouse_date_time: changeFormat(returningAndLeavingTime.splitReturningTime),
    leaving_warehouse_date_time: changeFormat(returningAndLeavingTime.splitLeavingTime)
  }
}

function changeFormat(dateTime: string | null) {
  return dateTime ? formatDateTimeWithSeconds(new Date(dateTime)) : null;
}

function errorHandler(error: any) {
  const res = error.response
  switch (res.status) {
    case 401:
      loginWithRedirect({ appState: { targetUrl: location.pathname } })
      break
    case 403:
      location.reload()
      break
    case 422:
      errorMessages.value = res.data.errors
      if (res.data.errors.delivery_plan_id?.length) {
        isRequested.value = true;
      }
  }
}

function moveDailyReportDisplay() {
  router.push({ name: "DailyReport" })
}

watch(splitReturningAndLeavingTime, () => {
  if (isRequested.value) {
    isRequested.value = false;
    errorMessages.value = {
      returning_warehouse_date_time: [],
      leaving_warehouse_date_time: [],
      delivery_plan_id: []
    };
  }
}, { deep: true });

</script>
<template>
  <div class="d-flex" style="min-height: 45vw">
    <div class="content p-4 w-100">
      <v-card class="my-4 pa-4">
        <h4 class="card-title mt-0 mb-4">
          {{ i18n.global.t('pages.dailyReports.splitForm.title') }}
        </h4>
        <v-row class="mt-2 mb-4 ml-12">
          <DisplayDeliveryPlanInformation
            :deliveryPlan="props.deliveryPlan"
          />
        </v-row>
        <v-row class="text-center">
          <DailyReportSplitFormDetail
            v-model="splitReturningAndLeavingTime"
            :deliveryPlan="props.deliveryPlan"
            :errorMessages="errorMessages"
          />
        </v-row>
        <div
          class="d-flex justify-content-end m-3"
        >
          <v-btn
            class="ma-2 reset-btn"
            :disabled="isLoading"
            @click="moveDailyReportDisplay"
          >
            {{ i18n.global.t('common.cancelBtn') }}
          </v-btn>
          <v-btn
            class="ma-2 exec-btn"
            :loading="isLoading"
            :disabled="!isActiveExecBtn"
            :class="execBtnClass"
            @click="isOpenDialog = true"
          >
            {{ i18n.global.t('common.confirmBtn') }}
          </v-btn>
        </div>
        <ValidationErrors
          class="d-flex justify-content-end"
          :messages="errorMessages.delivery_plan_id"
        >
        </ValidationErrors>
      </v-card>
    </div>
  </div>
  <RegisterModal
    :title="i18n.global.t('pages.dailyReports.splitConfirmModal.title')"
    v-model="isOpenDialog"
    :deliveryPlan="props.deliveryPlan"
    :splitReturningAndLeavingTime="splitReturningAndLeavingTime"
    :showFlash="props.showFlash"
    :execSplit="execSplit"
  />
  <CompleteModal
    v-model="isDisplayCompleteModal"
    :messages="[
      i18n.global.t('pages.dailyReports.splitCompleteModal.messageRow1'),
      i18n.global.t('pages.dailyReports.splitCompleteModal.messageRow2')
    ]"
    :moveDailyReportDisplay="moveDailyReportDisplay"
  />
</template>
<style scoped>
.exec-btn-disabled {
  background-color: #B3B8BB;
}
</style>
